import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, MailCheck } from 'lucide-react';
import { apiErrorText } from '@/lib/vaultUpload';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClaimFlatDialog({ projectId, open, onOpenChange, defaultEmail, onClaimed }) {
  const [flatNo, setFlatNo] = useState('');
  const [email, setEmail] = useState(defaultEmail || '');
  const [claim, setClaim] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = (o) => { if (!o) { setClaim(null); setCode(''); setFlatNo(''); } onOpenChange(o); };
  const start = async () => {
    setBusy(true);
    try {
      setClaim((await axios.post(`${API}/api/allottees/${projectId}/claim/start`, { flat_no: flatNo, email })).data);
    } catch (e) { toast.error(apiErrorText(e, 'Could not send the code')); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/allottees/claim/${claim.claim_id}/verify`, { code });
      toast.success(r.data.status === 'confirmed' ? 'Flat claimed. You can now confirm your details.' : 'Email verified. Next, upload your allotment letter or agreement so FBC can confirm your claim.');
      reset(false);
      onClaimed?.();
    } catch (e) { toast.error(apiErrorText(e, 'Could not verify')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-md" data-testid="claim-flat-dialog">
        <DialogHeader>
          <DialogTitle>Claim your flat</DialogTitle>
          <DialogDescription>We'll email you a 6-digit code to confirm the email address is yours.</DialogDescription>
        </DialogHeader>
        {!claim ? (
          <div className="space-y-3">
            <div><Label>Wing and flat number</Label><Input value={flatNo} onChange={(e) => setFlatNo(e.target.value)} placeholder="e.g. W9-1502" data-testid="claim-flat-no" /></div>
            <div><Label>Email for the code</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="claim-email" /></div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm flex items-center gap-2"><MailCheck className="h-4 w-4 text-emerald-600" />Code sent to {claim.sent_to} for flat {claim.flat_no}. It expires in 10 minutes.</p>
            <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="6-digit code" data-testid="claim-code" />
          </div>
        )}
        <DialogFooter>
          {claim && <Button variant="ghost" onClick={() => setClaim(null)} disabled={busy}>Back</Button>}
          {!claim
            ? <Button onClick={start} disabled={busy || !flatNo.trim() || !email.trim()} data-testid="claim-send-code">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Send code</Button>
            : <Button onClick={verify} disabled={busy || code.length !== 6} data-testid="claim-verify">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Verify</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
